import { Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowDown, ArrowUp, ClipboardCopy, ClipboardPaste, Copy, ExternalLink, GripVertical, IndentDecrease, IndentIncrease, Link2, ListPlus, Menu, Tag, Trash2, Unlink } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import { useRoleLabel } from "../links/useRoleLabel";
import { clsx } from "clsx";
import type { BlockIssue } from "../review/api";
import { usePopover } from "../ui/usePopover";
import { ResizeGrip } from "./ResizeGrip";
import { useT } from "../i18n";

export interface BlockChromeProps {
  children: ReactNode;
  depth: number;
  readOnly: boolean;
  focusedBy?: Array<{ name: string; color: string }>;
  workspaceId?: string;
  issues?: BlockIssue[];
  dragHandleProps?: Record<string, unknown>;
  setDragRef?: (el: HTMLElement | null) => void;
  style?: React.CSSProperties;
  /** part of the block selection (spec §8.18) */
  selected?: boolean;
  /** a click on the drag handle: Ctrl/⌘ toggles the block in the selection, Shift extends it */
  onHandleClick?: (e: React.MouseEvent) => void;
  /** where a block being dragged over this one would land (spec §8.6 item 4) */
  dropHint?: "before" | "after" | "into";
  /** [M18] the block's planning roles, shown as a pill (spec §8.6 item 6) */
  roles?: string[];
  /** [M21] the user-set height in px, or null when the block has never been resized (§8.6 item 8) */
  height?: number | null;
  /** [M21] what the height does: scroll the content inside it, or stretch the block into it */
  resize?: "scroll" | "fill";
  /** [M21] omitted where resizing does not belong: read-only pages and mirrored subtrees */
  onResize?: (px: number) => void;
  onResetHeight?: () => void;
}

/** Block chrome (spec §8.6 item 4): drag handle, presence names, conflict badge. The menu is `BlockMenu`. */
export function BlockChrome({ children, depth, readOnly, focusedBy, workspaceId, issues, dragHandleProps, setDragRef, style, selected, onHandleClick, dropHint, roles, height, resize = "scroll", onResize, onResetHeight }: BlockChromeProps) {
  // the grip measures the block's rendered height when there is no stored one yet (§8.6 item 8)
  const content = useRef<HTMLDivElement>(null);
  const t = useT();
  return (
    <div ref={setDragRef} style={{ ...style, paddingLeft: depth * 20 }} className="group relative flex gap-1 py-0.5" data-drop={dropHint}>
      {(dropHint === "before" || dropHint === "after") && <div className={clsx("pointer-events-none absolute right-0 z-20 h-0.5 bg-accent", dropHint === "before" ? "top-0" : "bottom-0")} style={{ left: depth * 20 + 28 }} data-testid="drop-line" />}
      <div className={clsx("flex w-6 shrink-0 flex-col items-center pt-1 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100", selected && "opacity-100")}>
        <button
          className={clsx("cursor-grab rounded text-neutral-400 hover:text-neutral-700", readOnly && "cursor-default", selected && "text-accent")}
          aria-label={t("block.drag")}
          aria-pressed={selected}
          title={readOnly ? t("block.drag.titleReadOnly") : t("block.drag.title")}
          data-testid="block-drag-handle"
          {...(readOnly ? {} : dragHandleProps)}
          onClick={onHandleClick}
        >
          <GripVertical size={16} />
        </button>
      </div>
      <div className={clsx("relative min-w-0 grow rounded border", dropHint === "into" ? "border-accent bg-sky-50 ring-2 ring-accent dark:bg-sky-950" : selected ? "border-accent ring-1 ring-accent" : "border-transparent hover:border-neutral-200 dark:hover:border-neutral-800")} data-selected={selected ? "true" : undefined}>
        {focusedBy && focusedBy.length > 0 && (
          <div className="flex gap-1 px-1 text-[10px]">
            {focusedBy.map((f) => (
              <span key={f.name} className="rounded px-1 text-white" style={{ background: f.color }}>
                {f.name}
              </span>
            ))}
          </div>
        )}
        <div
          ref={content}
          className={clsx(height && (resize === "fill" ? "kss-fill overflow-hidden" : "overflow-y-auto"))}
          style={height ? { height } : undefined}
          data-testid="block-body"
          data-resized={height ? "true" : undefined}
        >
          {children}
        </div>
        {/* the grip overlays the lower edge rather than taking a row of its own: an unresized block
            measures exactly as it did before M21, which the rail and the drop zones rely on */}
        {onResize && onResetHeight && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center">
            <ResizeGrip height={height ?? null} naturalHeight={() => content.current?.getBoundingClientRect().height ?? 240} onResize={onResize} onReset={onResetHeight} />
          </div>
        )}
      </div>
      {roles && roles.length > 0 && <RolePill roles={roles} />}
      {issues && issues.length > 0 && workspaceId && <ConflictBadge workspaceId={workspaceId} issues={issues} />}
    </div>
  );
}

/** What the block is (spec §8.6 item 6): the first role, plus a count when there are more. */
function RolePill({ roles }: { roles: string[] }) {
  const label = useRoleLabel(roles[0]!);
  return (
    <span
      className="mt-1 h-fit shrink-0 rounded-full border border-neutral-300 px-2 py-0.5 text-[10px] text-neutral-600 dark:border-neutral-700 dark:text-neutral-300"
      title={roles.join(", ")}
      data-testid="block-role"
      data-role={roles[0]}
    >
      {label}
      {roles.length > 1 ? ` +${roles.length - 1}` : ""}
    </span>
  );
}

export interface BlockMenuProps {
  iri: string;
  readOnly: boolean;
  onDelete(): void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  /** spec §8.6 item 4: become the last child of the group above / the parent's next sibling */
  onMoveIntoGroup?: () => void;
  onMoveOutOfGroup?: () => void;
  onLink?: () => void;
  /** [M18] "Set role…": what the block is (spec §8.11) */
  onSetRole?: () => void;
  sequences?: Array<{ id: string; label: string }>;
  onAddToSequence?: (id: string) => void;
  /** "Copy" or "Copy N blocks" when the block is part of a selection (spec §8.18) */
  copyLabel?: string;
  onCopy?: () => void;
  onPasteAfter?: () => void;
  onPasteInto?: () => void;
  /** a mirror block's own actions (spec §8.18) */
  mirror?: { onDetach?: () => void; original?: { workspaceId: string; docId: string; treeId?: string } };
  /** CSS classes placing the round button in the row below the block (spec §8.6 item 5) */
  className: string;
}

/** The round ☰ block menu button beside the add-block ＋ (spec §8.6 item 5) and its menu. */
export function BlockMenu({ iri, readOnly, onDelete, onMoveUp, onMoveDown, onMoveIntoGroup, onMoveOutOfGroup, onLink, onSetRole, sequences, onAddToSequence, copyLabel, onCopy, onPasteAfter, onPasteInto, mirror, className }: BlockMenuProps) {
  // dismissal (hover tolerance, press outside, Escape, one menu at a time) is shared: §8.6 item 5
  const { open, toggle, close: closeMenu, buttonRef, panelRef } = usePopover();
  const t = useT();
  const [seqOpen, setSeqOpen] = useState(false);
  return (
    <>
      <button
        ref={buttonRef}
        className={clsx("absolute -top-2 rounded-full border border-neutral-300 bg-white p-0.5 text-neutral-500 hover:text-neutral-900 dark:bg-neutral-900", className)}
        aria-label={t("block.menu")}
        title={t("block.menu")}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="block-menu-button"
        onClick={toggle}
      >
        <Menu size={14} />
      </button>
      {open && (
        <div ref={panelRef} className={clsx("absolute top-3 z-10 w-48 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700", className)} data-menu-open="" data-testid="block-menu">
          {/* a reader opens the same panel read-only (spec §8.6 item 5, Q64) */}
          <MenuItem icon={<Link2 size={14} />} label={readOnly ? t("block.menu.links") : t("block.menu.link")} disabled={!onLink} onClick={() => { closeMenu(); onLink?.(); }} testId="menu-link" />
          <MenuItem icon={<Tag size={14} />} label={t("block.menu.role")} disabled={!onSetRole} onClick={() => { closeMenu(); onSetRole?.(); }} testId="menu-role" />
          <MenuItem icon={<ListPlus size={14} />} label={t("block.menu.sequence")} disabled={readOnly || !onAddToSequence || !sequences?.length} onClick={() => setSeqOpen((v) => !v)} testId="menu-sequence" />
          {seqOpen &&
            sequences?.map((s) => (
              <button key={s.id} className="block w-full truncate rounded px-6 py-1 text-left text-xs hover:bg-neutral-100 dark:hover:bg-neutral-800" data-testid="menu-sequence-option" onClick={() => { closeMenu(); setSeqOpen(false); onAddToSequence?.(s.id); }}>
                {s.label || t("block.menu.untitledSequence")}
              </button>
            ))}
          <MenuItem icon={<ArrowUp size={14} />} label={t("block.menu.up")} disabled={readOnly || !onMoveUp} onClick={() => { closeMenu(); onMoveUp?.(); }} />
          <MenuItem icon={<ArrowDown size={14} />} label={t("block.menu.down")} disabled={readOnly || !onMoveDown} onClick={() => { closeMenu(); onMoveDown?.(); }} />
          {!readOnly && onMoveIntoGroup && <MenuItem icon={<IndentIncrease size={14} />} label={t("block.menu.intoGroup")} onClick={() => { closeMenu(); onMoveIntoGroup(); }} testId="menu-into-group" />}
          {!readOnly && onMoveOutOfGroup && <MenuItem icon={<IndentDecrease size={14} />} label={t("block.menu.outOfGroup")} onClick={() => { closeMenu(); onMoveOutOfGroup(); }} testId="menu-out-of-group" />}
          {onCopy && <MenuItem icon={<ClipboardCopy size={14} />} label={copyLabel ?? t("block.menu.copy")} onClick={() => { closeMenu(); onCopy(); }} testId="menu-copy" />}
          {onPasteAfter && <MenuItem icon={<ClipboardPaste size={14} />} label={t("block.menu.pasteAfter")} onClick={() => { closeMenu(); onPasteAfter(); }} testId="menu-paste-after" />}
          {onPasteInto && <MenuItem icon={<ClipboardPaste size={14} />} label={t("block.menu.pasteInto")} onClick={() => { closeMenu(); onPasteInto(); }} testId="menu-paste-into" />}
          {mirror?.original && (
            <Link to="/w/$workspaceId/d/$docId" params={{ workspaceId: mirror.original.workspaceId, docId: mirror.original.docId }} hash={mirror.original.treeId ? `block=${encodeURIComponent(mirror.original.treeId)}` : undefined} className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800" data-testid="menu-open-original">
              <ExternalLink size={14} /> {t("block.menu.openOriginal")}
            </Link>
          )}
          {mirror?.onDetach && <MenuItem icon={<Unlink size={14} />} label={t("block.menu.detach")} onClick={() => { closeMenu(); mirror.onDetach?.(); }} testId="menu-detach" />}
          <MenuItem icon={<Copy size={14} />} label={t("block.menu.copyIri")} onClick={() => void navigator.clipboard.writeText(iri)} testId="menu-copy-iri" />
          <MenuItem icon={<Trash2 size={14} />} label={t("block.menu.delete")} disabled={readOnly} onClick={onDelete} danger testId="menu-delete" />
        </div>
      )}
    </>
  );
}

const ISSUE_LABELS: Record<string, string> = {
  lww_conflict: "block.issues.lww_conflict",
  shacl_violation: "block.issues.shacl_violation",
  dangling_link: "block.issues.dangling_link",
  deprecated_term: "block.issues.deprecated_term",
  derived_edge: "block.issues.derived_edge",
  grounding: "block.issues.grounding",
};

/** Conflict badge (spec §8.6 item 4): open review items for this block, linking to the inbox. */
function ConflictBadge({ workspaceId, issues }: { workspaceId: string; issues: BlockIssue[] }) {
  const t = useT();
  const kinds = [...new Set(issues.map((i) => i.kind))];
  const conflicting = kinds.includes("lww_conflict");
  return (
    <Link
      to="/w/$workspaceId/review"
      params={{ workspaceId }}
      search={{ kind: conflicting ? "lww_conflict" : kinds[0] }}
      className={clsx(
        "mt-1 flex h-5 shrink-0 items-center gap-1 self-start rounded px-1.5 text-[11px]",
        conflicting ? "bg-amber-200 text-amber-900 dark:bg-amber-900 dark:text-amber-100" : "bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
      )}
      title={kinds.map((k) => (ISSUE_LABELS[k] ? t(ISSUE_LABELS[k]) : k)).join(", ")}
      data-testid="conflict-badge"
      data-kinds={kinds.join(" ")}
    >
      <AlertTriangle size={12} /> {issues.length}
    </Link>
  );
}

function MenuItem({ icon, label, onClick, disabled, title, danger, testId }: { icon: ReactNode; label: string; onClick?: () => void; disabled?: boolean; title?: string; danger?: boolean; testId?: string }) {
  return (
    <button className={clsx("flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-neutral-100 disabled:opacity-40 dark:hover:bg-neutral-800", danger && "text-red-700")} onClick={onClick} disabled={disabled} title={title} data-testid={testId}>
      {icon} {label}
    </button>
  );
}
